import { useEffect, useRef, useState } from "react";
import { AnimatedModal } from "../../components/AnimatedModal";
import { CloseIcon } from "../../components/CloseIcon";
import { H3 } from "../../components/Heading";
import useGqlClient from "../../hooks/useGqlClient";
import { Button } from "../../components/tailwind/Button";
import { Alert } from "../../components/Alert";
import { ArrowPathIcon, CheckCircleIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import {
  CreateDdqlDocument,
  CreateDdqlMutation,
  CreateDdqlMutationVariables,
  DataRoomFolder,
  DdqlDataRoomFolderSetting,
  S3DdqlUploadCompleteDocument,
  S3DdqlUploadCompleteMutation,
  S3DdqlUploadCompleteMutationVariables,
  useDealSettingsQuery,
} from "../../graphql/generated";
import { Spinner } from "../../components/icons/Spinner";
import { Uploader, getNumberOfParts } from "../../utils/multipartUploader";
import { TextInput } from "../../components/tailwind/TextInput";
import { toasts } from "../../components/toasts/toasts";
import { useHistory, useParams } from "react-router-dom";
import { Tooltip } from "@/src/components/Tooltip";
import { AddFolderPill } from "@/src/components/AddFolderPill";
import { links } from "@/src/utils/links";

export function NewDDQLModal(props: {
  dealFirmGroupId: string;
  parentDdqlID?: string;
  upload?: boolean;
  open: boolean;
  redirectOnSuccess?: boolean;
  onClose: () => void;
}) {
  const client = useGqlClient();

  const [file, setFile] = useState<File | null>(null);

  function clearForm() {
    setFile(null);
  }

  return (
    <FileUploader
      open={props.open}
      onClose={props.onClose}
      dealFirmGroupId={props.dealFirmGroupId}
      parentDdqlID={props.parentDdqlID || ""}
      upload={props.upload}
      redirectOnSuccess={props.redirectOnSuccess}
    />
  );
}

interface FileUploaderProps {
  open: boolean;
  error?: unknown;
  onClose: () => void;
  dealFirmGroupId: string;
  parentDdqlID?: string;
  upload?: boolean;
  redirectOnSuccess?: boolean;
}

interface FileWrapper {
  id: number;
  file: File;
  name: string;
  uploadProgress: number;
  status: "creating" | "uploading" | "complete" | "error" | "pending";
}

function FileUploader(props: FileUploaderProps) {
  const [dragActive, setDragActive] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  const [files, setFiles] = useState<FileWrapper[]>([]);
  const [fileError, setFileError] = useState("");

  const createButtonRef = useRef<HTMLButtonElement>(null);

  const queryClient = useQueryClient();
  const [ddqlName, setDdqlName] = useState("");
  const [ddqlNameError, setDdqlNameError] = useState("");

  const client = useGqlClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();

  const dealSettingsQuery = useDealSettingsQuery(client, {
    dealID: activeDealId,
  });

  const [uploadingFile, setUploadingFile] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<DataRoomFolder | null>(
    null,
  );

  const history = useHistory();

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) {
      if (event.key === "Enter") {
        event.preventDefault();
        if (createButtonRef.current) {
          createButtonRef.current.click();
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleDrag = function (e: any) {
    setFileError("");
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = function (e: any) {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
      const newFiles: FileWrapper[] = Array.from<File>(
        e.dataTransfer.files,
      ).map((f, i) => ({
        id: i,
        file: f,
        name: f.name,
        uploadProgress: 0,
        status: "pending",
      }));
      setFiles(newFiles);
    }
  };

  function createDdql(fileWrap?: FileWrapper): Promise<string> {
    setUploadingFile(true);
    let ddqlId = "";

    return new Promise((resolve, reject) => {
      try {
        setFiles((prev) => {
          return prev.map((f) => {
            if (fileWrap && f.id === fileWrap.id) {
              return {
                ...f,
                status: "creating",
              };
            }

            return f;
          });
        });

        client
          .request<CreateDdqlMutation, CreateDdqlMutationVariables>(
            CreateDdqlDocument,
            {
              input: {
                name: ddqlName,
                dealGroupID: props.dealFirmGroupId,
                dataRoomFolderID: selectedFolder ? selectedFolder.id : null,
                ...(props.parentDdqlID && { parentDdqlID: props.parentDdqlID }),
                file: fileWrap
                  ? {
                      fileName: fileWrap.file.name,
                      fileType: fileWrap.file.type,
                      parts: getNumberOfParts(fileWrap.file),
                    }
                  : null,
              },
            },
          )
          .catch((e) => {
            setFiles((prev) => {
              return prev.map((f) => {
                if (fileWrap && f.id === fileWrap.id) {
                  return {
                    ...f,
                    status: "error",
                  };
                }

                return f;
              });
            });
          })
          .then((data) => {
            if (!data || !fileWrap) {
              if (!data) {
                setError("Failed to create DDQL");
                return;
              }
              setUploadingFile(false);
              clearForm();
              props.onClose();
              toasts.success("DDQL created successfully");
              ddqlId = data.createDdql.ddql.id;
              queryClient.invalidateQueries({
                queryKey: ["Questions", { dealId: activeDealId }],
              });

              resolve(ddqlId);
              return;
            }

            // Store the ddqlId for when file upload completes
            ddqlId = data.createDdql.ddql.id;

            setFiles((prev) => {
              return prev.map((f) => {
                if (fileWrap && f.id === fileWrap.id) {
                  return {
                    ...f,
                    status: "uploading",
                  };
                }

                return f;
              });
            });

            const uploader = new Uploader({
              file: fileWrap.file,
              uploadId: data.createDdql.multiPartUploadID,
              parts: data.createDdql.parts.map((p) => {
                return {
                  PartNumber: p.partNumber,
                  signedUrl: p.presignedUrl,
                };
              }),
            });

            uploader.start();

            uploader.onError(() => {
              setFiles((prev) => {
                return prev.map((f) => {
                  if (f.id === fileWrap.id) {
                    return {
                      ...f,
                      status: "error",
                    };
                  }

                  return f;
                });
              });
              queryClient.invalidateQueries({
                queryKey: ["Questions", { dealId: activeDealId }],
              });
              resolve(ddqlId);
            });

            uploader.onProgress((progress) => {
              setFiles((prev) => {
                return prev.map((f) => {
                  if (f.id === fileWrap.id) {
                    return {
                      ...f,
                      uploadProgress: progress.percentage,
                    };
                  }

                  return f;
                });
              });
            });

            uploader.onComplete((uploadedParts) => {
              setUploadingFile(false);
              client
                .request<
                  S3DdqlUploadCompleteMutation,
                  S3DdqlUploadCompleteMutationVariables
                >(S3DdqlUploadCompleteDocument, {
                  ddqlID: data.createDdql.ddql.id,
                  multiPartUploadID: data.createDdql.multiPartUploadID,
                  parts: uploadedParts.map((up) => {
                    return {
                      partNumber: up.PartNumber,
                      etag: up.ETag,
                    };
                  }),
                })
                .then((d) => {
                  if (!d) {
                    return;
                  }
                  setFiles((prev) => {
                    return prev.map((f) => {
                      if (f.id === fileWrap.id) {
                        return {
                          ...f,
                          status: "complete",
                        };
                      }

                      return f;
                    });
                  });
                  queryClient.invalidateQueries({
                    queryKey: ["Questions", { dealId: activeDealId }],
                  });
                  resolve(ddqlId);
                });
            });
          });
      } catch (error) {
        queryClient.invalidateQueries({
          queryKey: ["Questions", { dealId: activeDealId }],
        });

        resolve("");
      }
    });
  }

  function clearForm() {
    setFiles([]);
    setError("");
    setFileError("");
    setDdqlName("");
    setSelectedFolder(null);
  }

  return (
    <AnimatedModal
      padding="p-0"
      open={props.open}
      onClose={() => {
        clearForm();
        props.onClose();
      }}
      size="lg"
    >
      <div>
        <div className="p-4 flex justify-between items-center">
          <div>
            <H3>{props.parentDdqlID ? "New section" : "New question list"}</H3>
          </div>
          <CloseIcon
            onClose={() => {
              clearForm();
              props.onClose();
            }}
          />
        </div>
        <form
          onSubmit={async (e) => {
            e.preventDefault();

            if (ddqlName.trim() === "") {
              setDdqlNameError("Name is required");
              return;
            }

            if (
              !props.parentDdqlID &&
              dealSettingsQuery.data &&
              dealSettingsQuery.data.deal.settings.ddqlDataRoomFolder ===
                DdqlDataRoomFolderSetting.Required &&
              !selectedFolder
            ) {
              setError("Please select a Data Room folder");
              return;
            }

            const f = files.length > 0 ? files[0] : undefined;
            const ddqlId = await createDdql(f);

            if (ddqlId && props.redirectOnSuccess) {
              history.push({
                pathname: links.ddqlLink({ id: ddqlId }, activeDealId),
              });
            }
          }}
        >
          <div className="px-3 ">
            <TextInput
              label="Name"
              placeholder="Due Diligence Questionnaire"
              secondaryLabel="Required"
              value={ddqlName}
              error={ddqlNameError}
              onChange={(e) => {
                setDdqlNameError("");
                setDdqlName(e.currentTarget.value);
              }}
            />

            {dealSettingsQuery.data &&
            dealSettingsQuery.data.deal.settings.ddqlDataRoomFolder ===
              DdqlDataRoomFolderSetting.Required &&
            !props.parentDdqlID ? (
              <div className="mt-5">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold leading-3 text-gray-900 flex items-center gap-x-1">
                    Data Room folder
                    <Tooltip
                      text="Any documents uploaded to answers inside this question list will be automatically uploaded to this folder"
                      width="w-72"
                    />
                  </p>
                  <span className="text-gray-500 text-xs font-normal">
                    {dealSettingsQuery.data &&
                    dealSettingsQuery.data.deal.settings.ddqlDataRoomFolder ===
                      DdqlDataRoomFolderSetting.Required
                      ? "Required"
                      : "Optional"}
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Select a folder from the Data Room for files uploaded to this
                  question list to be stored in
                </p>

                <div className="mt-2">
                  <AddFolderPill
                    onFolderSelected={(folder) => {
                      setError("");
                      setSelectedFolder(folder);
                    }}
                    selectedFolder={selectedFolder}
                  />
                </div>
              </div>
            ) : null}

            <div className=" bg-gray-200 mt-6 w-full shadow-inner h-px" />
            {error ? (
              <Alert margin="m 0 0 0" type="error" text={error} />
            ) : null}
            <div className="p-4 flex justify-end">
              {files.length > 0 &&
              files.every((f) => ["complete", "error"].includes(f.status)) ? (
                <FilesSummary
                  files={files}
                  onRetry={() => {
                    Promise.all(
                      files
                        .filter((f) => f.status === "error")
                        .map((f) => createDdql(f)),
                    );
                  }}
                  onCompleted={() => {
                    clearForm();
                    props.onClose();
                  }}
                />
              ) : (
                <Button
                  ref={createButtonRef}
                  type="submit"
                  variant="positive"
                  text={
                    props.parentDdqlID
                      ? "Create new section"
                      : "Create new list"
                  }
                  isLoading={
                    uploadingFile ||
                    files.some((f) =>
                      ["creating", "uploading"].includes(f.status),
                    )
                  }
                  loadingText="Creating..."
                  onClick={() => {}}
                  isDisabled={
                    ddqlName.trim() === "" ||
                    (!props.parentDdqlID &&
                      dealSettingsQuery.data &&
                      dealSettingsQuery.data.deal.settings
                        .ddqlDataRoomFolder ===
                        DdqlDataRoomFolderSetting.Required &&
                      !selectedFolder)
                  }
                />
              )}
            </div>
          </div>
        </form>
      </div>
    </AnimatedModal>
  );
}

function FileWrapStatus(props: { fileWrap: FileWrapper; onRetry: () => void }) {
  if (props.fileWrap.status === "creating") {
    return (
      <div className="flex items-center gap-x-1.5">
        <Spinner color="gray" size="s" />
        <p className="text-sm text-gray-500/80 font-semibold">
          Creating file...
        </p>
      </div>
    );
  }

  if (props.fileWrap.status === "uploading") {
    // retunr a progress bar

    return (
      <div className="w-32">
        <ProgressBar progress={props.fileWrap.uploadProgress} />
      </div>
    );
  }

  if (props.fileWrap.status === "error") {
    return (
      <div className="">
        <p className="text-xs text-orange-500">Error uploading file</p>
        <button
          className="flex items-center gap-x-1.5 text-gray-400 hover:text-gray-500"
          onClick={() => {
            props.onRetry();
          }}
        >
          <ArrowPathIcon className="h-5 w-5" />
          <p className="text-sm font-semibold">Try again</p>
        </button>
      </div>
    );
  }

  if (props.fileWrap.status === "complete") {
    return (
      <div>
        <CheckCircleIcon className="h-5 w-5 text-green-500" />
      </div>
    );
  }

  return null;
}

function FilesSummary(props: {
  files: FileWrapper[];
  onRetry: () => void;
  onCompleted: () => void;
}) {
  const queryClient = useQueryClient();
  if (props.files.some((f) => f.status === "error")) {
    return (
      <div className="flex items-center gap-x-2">
        <p className="text-sm text-gray-500/80">
          Failed to upload{" "}
          {props.files.filter((f) => f.status === "error").length} files
        </p>
        <Button
          text="Retry"
          variant="neutral"
          onClick={() => {
            props.onRetry();
          }}
        />
        <Button
          text="Done"
          variant="positive"
          onClick={() => {
            props.onCompleted();
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-x-2">
      <p className="text-sm text-gray-500/80">
        {props.files.length} files uploaded successfully
      </p>
      <Button
        text="Done"
        variant="positive"
        type="button"
        onClick={() => {
          props.onCompleted();
        }}
      />
    </div>
  );
}

function ProgressBar(props: { progress: number }) {
  return (
    <div className="h-1 w-full bg-blue-500/50 rounded-md">
      <div
        className="h-1 bg-blue-500 rounded-md transition-all duration-300 ease-in-out"
        style={{ width: `${props.progress}%` }}
      ></div>
    </div>
  );
}
